// Precondition: None
// Postcondition: Custom element class for toast notifications
// Invariant: Only one toast container exists, toasts stack vertically

import { icon } from '../../../shared/icons.js';

export interface ToastOptions {
  message: string;
  type?: "default" | "info" | "success" | "warning" | "error";
  duration?: number;
  undoCallback?: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  type: string;
  undoCallback?: () => void;
  element?: HTMLElement;
}

class FtToast extends HTMLElement {
  private toasts: ToastItem[] = [];
  private nextId = 0;
  private abortController = new AbortController();

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
    // Use Popover API to ensure toasts appear above dialogs (top layer)
    this.setAttribute("popover", "manual");
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        pointer-events: none;
        width: 100%;
        max-width: 420px;
        /* Override default popover styles */
        margin: 0;
        padding: 0;
        border: none;
        background: transparent;
        overflow: visible;
      }

      @media (max-width: 480px) {
        :host {
          bottom: 16px;
          left: 16px;
          right: 16px;
          transform: none;
          max-width: none;
          width: auto;
        }
      }

      .container {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      /* 3D toast wrapper */
      .toast {
        position: relative;
        pointer-events: auto;
      }

      .shadow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 8px;
        background: hsl(0deg 0% 0% / 0.25);
        filter: blur(4px);
        transform: translateY(2px);
      }

      .edge {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 8px;
      }

      .front {
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-radius: 8px;
        font: 400 14px/1.4 Inter, system-ui, sans-serif;
        transform: translateY(-4px);
      }

      @media (prefers-reduced-motion: no-preference) {
        .toast {
          animation: toast-in 150ms ease-out;
        }
        .toast.removing {
          animation: toast-out 100ms ease-in forwards;
        }
      }

      @keyframes toast-in {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes toast-out {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(8px); }
      }

      /* Default */
      .toast.default .edge {
        background: linear-gradient(to left, hsl(0deg 0% 70%) 0%, hsl(0deg 0% 85%) 8%, hsl(0deg 0% 85%) 92%, hsl(0deg 0% 70%) 100%);
      }
      .toast.default .front {
        background: #ffffff;
        color: #16171a;
      }
      @media (prefers-color-scheme: dark) {
        .toast.default .edge {
          background: linear-gradient(to left, hsl(0deg 0% 8%) 0%, hsl(0deg 0% 18%) 8%, hsl(0deg 0% 18%) 92%, hsl(0deg 0% 8%) 100%);
        }
        .toast.default .front {
          background: #202021;
          color: #eeeff1;
        }
      }

      /* Info: blue */
      .toast.info .edge {
        background: linear-gradient(to left, hsl(207deg 100% 35%) 0%, hsl(207deg 100% 50%) 8%, hsl(207deg 100% 50%) 92%, hsl(207deg 100% 35%) 100%);
      }
      .toast.info .front {
        background: #0091ff;
        color: #ffffff;
      }

      /* Success: green */
      .toast.success .edge {
        background: linear-gradient(to left, hsl(142deg 71% 35%) 0%, hsl(142deg 71% 45%) 8%, hsl(142deg 71% 45%) 92%, hsl(142deg 71% 35%) 100%);
      }
      .toast.success .front {
        background: #34c759;
        color: #ffffff;
      }

      /* Warning: amber */
      .toast.warning .edge {
        background: linear-gradient(to left, hsl(38deg 100% 45%) 0%, hsl(38deg 100% 63%) 8%, hsl(38deg 100% 63%) 92%, hsl(38deg 100% 45%) 100%);
      }
      .toast.warning .front {
        background: #ffb340;
        color: #16171a;
      }

      /* Error: red */
      .toast.error .edge {
        background: linear-gradient(to left, hsl(354deg 91% 45%) 0%, hsl(354deg 91% 63%) 8%, hsl(354deg 91% 63%) 92%, hsl(354deg 91% 45%) 100%);
      }
      .toast.error .front {
        background: #f5455c;
        color: #ffffff;
      }

      .message {
        flex: 1;
        min-width: 0;
      }

      .actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .undo-button,
      .close-button {
        background: transparent;
        border: none;
        color: inherit;
        cursor: pointer;
        border-radius: 4px;
        opacity: 0.8;
      }

      @media (prefers-reduced-motion: no-preference) {
        .undo-button,
        .close-button {
          transition: opacity 100ms ease-out;
        }
      }

      .undo-button:hover,
      .close-button:hover {
        opacity: 1;
      }

      .undo-button {
        font: 500 14px Inter, system-ui, sans-serif;
        padding: 4px 8px;
        margin: -4px 0;
      }

      .close-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        margin: -4px;
      }

      .close-icon {
        display: flex;
        width: 16px;
        height: 16px;
      }
    `;
  }

  private getTemplate(): string {
    return `<div class="container"></div>`;
  }

  private setupEventListeners(): void {
    const signal = this.abortController.signal;

    // Listen for custom toast events on document
    document.addEventListener(
      "ft-toast",
      ((e: CustomEvent<ToastOptions>) => {
        this.show(e.detail);
      }) as EventListener,
      { signal }
    );
  }

  // Public API: Show a toast notification
  // Precondition: options.message is non-empty
  // Postcondition: Toast is displayed and auto-dismissed after duration
  show(options: ToastOptions): number {
    const id = this.nextId++;
    const type = options.type || "default";
    const duration = options.duration ?? 5000;

    const toast: ToastItem = {
      id,
      message: options.message,
      type,
      undoCallback: options.undoCallback,
    };

    const wasEmpty = this.toasts.length === 0;
    this.toasts.push(toast);
    this.renderToast(toast);

    // Show popover when first toast is added
    if (wasEmpty) {
      this.showPopover();
    }

    // Auto-dismiss after duration (0 = no auto-dismiss)
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }

    return id;
  }

  private renderToast(toast: ToastItem): void {
    const container = this.shadowRoot?.querySelector(".container");
    if (!container) return;

    // 3D toast structure: wrapper > shadow + edge + front
    const el = document.createElement("div");
    el.className = `toast ${toast.type}`;
    el.setAttribute("role", "alert");
    el.setAttribute("aria-live", "polite");
    el.dataset.toastId = String(toast.id);

    const shadow = document.createElement("span");
    shadow.className = "shadow";
    el.appendChild(shadow);

    const edge = document.createElement("span");
    edge.className = "edge";
    el.appendChild(edge);

    const front = document.createElement("div");
    front.className = "front";

    const messageEl = document.createElement("span");
    messageEl.className = "message";
    messageEl.textContent = toast.message;
    front.appendChild(messageEl);

    const actionsEl = document.createElement("div");
    actionsEl.className = "actions";

    if (toast.undoCallback) {
      const undoBtn = document.createElement("button");
      undoBtn.className = "undo-button";
      undoBtn.textContent = "Undo";
      undoBtn.addEventListener("click", () => {
        toast.undoCallback?.();
        this.dismiss(toast.id);
      });
      actionsEl.appendChild(undoBtn);
    }

    const closeBtn = document.createElement("button");
    closeBtn.className = "close-button";
    closeBtn.setAttribute("aria-label", "Dismiss");
    closeBtn.innerHTML = `<span class="close-icon">${icon('x', 16)}</span>`;
    closeBtn.addEventListener("click", () => this.dismiss(toast.id));
    actionsEl.appendChild(closeBtn);

    front.appendChild(actionsEl);
    el.appendChild(front);
    container.appendChild(el);
    toast.element = el;
  }

  // Public API: Dismiss a toast by ID
  // Precondition: id is a valid toast ID
  // Postcondition: Toast is removed with animation
  dismiss(id: number): void {
    const toast = this.toasts.find((t) => t.id === id);
    if (!toast || !toast.element) return;

    const remove = () => {
      toast.element?.remove();
      this.toasts = this.toasts.filter((t) => t.id !== id);
      // Hide popover when last toast is removed
      if (this.toasts.length === 0) {
        this.hidePopover();
      }
    };

    // Check if animations are enabled
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      remove();
    } else {
      toast.element.classList.add("removing");
      toast.element.addEventListener("animationend", remove, { once: true });
    }
  }

  // Public API: Dismiss all toasts
  // Precondition: None
  // Postcondition: All toasts are removed
  dismissAll(): void {
    [...this.toasts].forEach((toast) => this.dismiss(toast.id));
  }
}

// Register custom element with guard
if (!customElements.get("ft-toast")) {
  customElements.define("ft-toast", FtToast);
}

// Helper function to show toast from anywhere
// Usage: showToast({ message: 'File deleted', type: 'success', undoCallback: () => restore() })
export function showToast(options: ToastOptions): void {
  document.dispatchEvent(
    new CustomEvent("ft-toast", {
      detail: options,
      bubbles: true,
    })
  );
}
